import { useEffect, useRef, useSyncExternalStore } from 'react';
import Ajv from 'ajv';
import { ChatMode, FETool } from '../types';

export const UI_CONTEXT_SELECTED_ITEMS_LIMIT = 50;

export type PageUpdatePreviewRow = {
  field: string;
  label: string;
  value: string;
};

export type UiContext = {
  description: string;
  handler: (args: Record<string, unknown>) => unknown | Promise<unknown>;
  argsSchema?: FETool['argsSchema'];
  requiresApproval?: boolean;
  enabledModes?: ChatMode[];
  preview?: (
    proposed: Record<string, unknown>,
  ) => PageUpdatePreviewRow[] | undefined;
};

type UiContextResult = {
  ok: boolean;
  result?: unknown;
  error?: string;
  nextSteps?: string;
};

const tools = new Map<string, UiContext>();
const listeners = new Set<() => void>();
let snapshot: FETool[] = [];

const toFETool = (name: string, entry: UiContext): FETool => ({
  name,
  description: entry.description,
  hasOutput: true,
  argsSchema: entry.argsSchema ?? {
    type: 'object',
    properties: {},
  },
  enabledModes: entry.enabledModes,
});

const notify = (): void => {
  snapshot = [...tools.entries()]
    .map(([name, entry]) => toFETool(name, entry))
    .sort((left, right) => left.name.localeCompare(right.name));
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getUiContextTools = (): FETool[] => snapshot;

export const useUiContext = (next: Record<string, UiContext>): void => {
  const entriesRef = useRef(next);
  entriesRef.current = next;

  const namesKey = Object.keys(next).sort().join(',');

  useEffect(() => {
    const names = namesKey ? namesKey.split(',') : [];
    if (names.length === 0) return undefined;

    names.forEach((name) => {
      const entry = entriesRef.current[name];
      tools.set(name, {
        description: entry.description,
        argsSchema: entry.argsSchema,
        requiresApproval: entry.requiresApproval,
        enabledModes: entry.enabledModes,
        preview: (proposed) => entriesRef.current[name]?.preview?.(proposed),
        handler: (args) => {
          const current = entriesRef.current[name];
          if (!current) {
            throw new Error(`${name} is not available on the current page`);
          }
          return current.handler(args);
        },
      });
    });
    notify();
    return () => {
      names.forEach((name) => {
        tools.delete(name);
      });
      notify();
    };
  }, [namesKey]);
};

export const useUiContextTools = () =>
  useSyncExternalStore(subscribe, getUiContextTools, getUiContextTools);

export const requiresUiContextApproval = (name: string): boolean =>
  tools.get(name)?.requiresApproval === true;

export const previewUiContextUpdate = (
  name: string,
  proposed?: Record<string, unknown>,
): PageUpdatePreviewRow[] | undefined => {
  if (!proposed) {
    return undefined;
  }
  return tools.get(name)?.preview?.(proposed);
};

const ajv = new Ajv({ allErrors: true, strict: false });

const formatAjvErrors = (
  errors: { instancePath?: string; message?: string }[] | null | undefined,
) =>
  (errors ?? [])
    .map((error) => {
      if (!error.message) {
        return undefined;
      }
      return error.instancePath
        ? `${error.instancePath} ${error.message}`
        : error.message;
    })
    .filter((message): message is string => !!message)
    .join('; ');

const OFF_PAGE_NEXT_STEPS =
  'Ask the user to open the relevant page and try again.';

export const runUiContext = async (
  name: string,
  args: Record<string, unknown> = {},
): Promise<UiContextResult> => {
  const entry = tools.get(name);

  if (!entry) {
    return {
      ok: false,
      error: `${name} is not available on the current page`,
      nextSteps: OFF_PAGE_NEXT_STEPS,
    };
  }

  if (entry.argsSchema) {
    const validate = ajv.compile(entry.argsSchema);
    if (!validate(args)) {
      return {
        ok: false,
        error: `Invalid arguments: ${formatAjvErrors(validate.errors)}`,
        nextSteps: 'Fix the arguments and retry.',
      };
    }
  }

  try {
    const result = await entry.handler(args);
    return { ok: true, result };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Action failed';
    return {
      ok: false,
      error: message,
      nextSteps: 'Read the error and retry or ask the user for help.',
    };
  }
};

export const resetUiContextForTests = (): void => {
  tools.clear();
  listeners.clear();
  notify();
};
